import React, { FC, Fragment, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from 'components/shared/loader/loader.component';
import {
    ProductionOverviewCountMode,
    ProductionOverviewPeriod,
    ProductionOverviewRowKey,
} from 'models/production-overview';
import OverviewFilterBar, { OverviewFilterState } from './overview-filter-bar.component';
import OverviewCalendarTable from './overview-calendar-table.component';
import InProductionSection from './in-production-section.component';
import OverviewOrdersDialog, { OverviewOrdersDialogState } from './overview-orders-dialog.component';
import { useProductionOverviewData } from './use-production-overview-data.hook';
import {
    OverviewCalendarGroup,
    buildDayKeys,
    dayCountsToMap,
    formatDayKey,
    resolveDateRange,
    validateCustomRange,
} from './production-overview-utils';

const DIALOG_SEARCH_PARAM = 'overviewCell';

const parseDialogState = (searchValue: string | null): OverviewOrdersDialogState | null => {
    if (!searchValue) return null;
    try {
        const parsed = JSON.parse(searchValue);
        if (Array.isArray(parsed?.labelParts) && Array.isArray(parsed?.cells)) return parsed;
    } catch {
        // A hand-edited URL is not an error — the dialog simply stays closed.
    }
    return null;
};

const ProductionOverview: FC = () => {
    const { t: translate } = useTranslation(['production-overview', 'common']);
    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState<OverviewFilterState>({
        period: ProductionOverviewPeriod.FOURTEEN_DAYS,
        startDate: new Date(),
        customFromDate: null,
        customToDate: null,
        clinic: null,
        countMode: ProductionOverviewCountMode.ORDERS,
    });

    const rangeValidationError = validateCustomRange(filters.customFromDate, filters.customToDate);
    const dateRange = resolveDateRange(filters.period, filters.startDate, filters.customFromDate, filters.customToDate);
    const clinicId = filters.clinic?._id;

    const { inbox, received, production, isLoading, error } = useProductionOverviewData({
        fromDate: dateRange ? formatDayKey(dateRange.fromDate) : '',
        toDate: dateRange ? formatDayKey(dateRange.toDate) : '',
        clinicId,
        skip: !dateRange,
    });

    const days = useMemo(
        () => (dateRange ? buildDayKeys(dateRange.fromDate, dateRange.toDate) : []),
        [dateRange?.fromDate.getTime(), dateRange?.toDate.getTime()],
    );

    const groups: OverviewCalendarGroup[] = useMemo(
        () => [
            {
                groupKey: 'inbox',
                labelKey: 'GROUP_INBOX',
                rows: [],
                showDayTotals: true,
                pointInTimeHeaderCount: inbox,
                headerRowKey: ProductionOverviewRowKey.INBOX,
            },
            {
                groupKey: 'received',
                labelKey: 'GROUP_RECEIVED',
                showDayTotals: true,
                groupTotalRowKey: ProductionOverviewRowKey.RECEIVED_ALL,
                rows: [
                    {
                        rowKey: ProductionOverviewRowKey.RECEIVED_BY_CUSTOMER,
                        labelKey: 'ROW_CREATED_BY_CUSTOMER',
                        countsByDay: dayCountsToMap(received?.byCustomer),
                    },
                    {
                        rowKey: ProductionOverviewRowKey.RECEIVED_SCANNER_CASE,
                        labelKey: 'ROW_VIA_SCANNER_CASE',
                        countsByDay: dayCountsToMap(received?.viaScannerCase),
                    },
                    {
                        rowKey: ProductionOverviewRowKey.RECEIVED_ORDER_FORM,
                        labelKey: 'ROW_VIA_ORDER_FORM',
                        countsByDay: dayCountsToMap(received?.viaOrderForm),
                    },
                    {
                        rowKey: ProductionOverviewRowKey.RECEIVED_MANUAL,
                        labelKey: 'ROW_CREATED_MANUALLY',
                        countsByDay: dayCountsToMap(received?.createdManually),
                    },
                ],
            },
            {
                groupKey: 'production',
                labelKey: 'GROUP_PRODUCTION',
                showDayTotals: false,
                rows: [
                    {
                        rowKey: ProductionOverviewRowKey.PRODUCTION_FINISHED,
                        labelKey: 'ROW_PRODUCTION_FINISHED',
                        countsByDay: dayCountsToMap(production?.productionFinished),
                    },
                    {
                        rowKey: ProductionOverviewRowKey.SENT_TO_CLINIC,
                        labelKey: 'ROW_SENT_TO_CLINIC',
                        countsByDay: dayCountsToMap(production?.sentToClinic),
                    },
                    {
                        rowKey: ProductionOverviewRowKey.DEADLINE,
                        labelKey: 'ROW_DEADLINE',
                        countsByDay: dayCountsToMap(production?.deadline),
                        lateToday: production?.lateToday,
                    },
                    {
                        rowKey: ProductionOverviewRowKey.WITHOUT_DEADLINE,
                        labelKey: 'ROW_WITHOUT_DEADLINE',
                        countsByDay: dayCountsToMap(
                            production?.withoutDeadline
                                ? [{ ...production.withoutDeadline, day: formatDayKey(new Date()) }]
                                : [],
                        ),
                        pointInTimeOnly: true,
                    },
                    {
                        rowKey: ProductionOverviewRowKey.DELIVERY_TO_CUSTOMER,
                        labelKey: 'ROW_DELIVERY_TO_CUSTOMER',
                        countsByDay: dayCountsToMap(production?.deliveryToCustomer),
                    },
                ],
            },
        ],
        [inbox, received, production],
    );

    const dialogState = parseDialogState(searchParams.get(DIALOG_SEARCH_PARAM));

    const openOrdersDialog: (labelParts: string[], cells: OverviewOrdersDialogState['cells']) => void = (
        labelParts,
        cells,
    ) => {
        const cellsWithClinic = cells.map((cell) => (clinicId ? { ...cell, clinicId } : cell));
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(DIALOG_SEARCH_PARAM, JSON.stringify({ labelParts, cells: cellsWithClinic }));
        setSearchParams(nextParams);
    };

    const closeOrdersDialog = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(DIALOG_SEARCH_PARAM);
        setSearchParams(nextParams);
    };

    const hasAnyData = Boolean(inbox || received || production);

    return (
        <Fragment>
            <OverviewFilterBar
                filters={filters}
                onFiltersChange={setFilters}
                rangeValidationError={rangeValidationError}
            />

            {error ? (
                <Typography variant="body2" color="error" data-test="production-overview.load-error">
                    {translate('production-overview:OVERVIEW_LOAD_ERROR')}
                </Typography>
            ) : undefined}

            {!dateRange && !rangeValidationError ? (
                <Typography variant="body2" color="text.secondary">
                    {translate('production-overview:PICK_CUSTOM_RANGE')}
                </Typography>
            ) : undefined}

            {isLoading && !hasAnyData ? <Loader /> : undefined}

            {dateRange && hasAnyData ? (
                <Box>
                    <OverviewCalendarTable
                        groups={groups}
                        days={days}
                        countMode={filters.countMode}
                        isCompact={days.length > 14}
                        onCellClick={openOrdersDialog}
                    />
                </Box>
            ) : undefined}

            {/* The snapshot is a right-now view: it ignores the chosen date and period. */}
            <InProductionSection clinicId={clinicId} onCellClick={openOrdersDialog} />

            {dialogState ? (
                <OverviewOrdersDialog
                    dialogState={dialogState}
                    countMode={filters.countMode}
                    onClose={closeOrdersDialog}
                />
            ) : undefined}
        </Fragment>
    );
};

export default ProductionOverview;
